/**
 * Uyarıcı çizimi.
 *
 * Her görevin uyarıcısı kendi biçiminde gelir; burada ekrana çevrilir.
 * İçerik uyarıcı GÖRÜNMEDEN ÖNCE çizilir ve gizli durur; deneme anında
 * yalnızca görünürlük değişir. Böylece gösterim anı React'in değil
 * requestAnimationFrame'in kontrolünde kalır.
 */

import type { TrialSpec } from '@nirengi/engine'

const COLOR_HEX: Record<string, string> = {
  mavi: '#3b74e8',
  kirmizi: '#d94b4b',
  yesil: '#3fae5a',
  sari: '#d9b63b',
}

const SHAPE_GLYPH: Record<string, string> = {
  daire: '●',
  kare: '■',
  ucgen: '▲',
  yildiz: '★',
  altigen: '⬢',
  kalp: '♥',
}

const ARROW: Record<string, string> = { sol: '◀', sag: '▶' }

export function Stimulus({ spec }: { spec: TrialSpec | null }) {
  if (!spec) return null
  const s = spec.stimulus as Record<string, unknown>

  switch (s['kind']) {
    case 'letter':
      return <div className="glyph">{String(s['letter'])}</div>

    case 'flanker': {
      const n = Number(s['flankerCount'])
      const side = Math.max(1, Math.floor(n / 2))
      const flank = ARROW[String(s['flank'])] ?? '▶'
      const dir = ARROW[String(s['dir'])] ?? '▶'
      return (
        <div className="arrows">
          {Array.from({ length: side }, (_, i) => (
            <span key={'l' + i}>{flank}</span>
          ))}
          <span className="center">{dir}</span>
          {Array.from({ length: side }, (_, i) => (
            <span key={'r' + i}>{flank}</span>
          ))}
        </div>
      )
    }

    case 'symbol_search': {
      const size = Number(s['gridSize'])
      const items = s['items'] as string[]
      return (
        <div className="sgrid" style={{ gridTemplateColumns: `repeat(${size}, auto)` }}>
          {items.map((g, i) => (
            <div className="c" key={i}>
              {SHAPE_GLYPH[g] ?? g}
            </div>
          ))}
        </div>
      )
    }

    case 'rule_switch': {
      const color = COLOR_HEX[String(s['color'])] ?? '#555'
      const shape = SHAPE_GLYPH[String(s['shape'])] ?? '●'
      return (
        <div style={{ display: 'grid', gap: 12, justifyItems: 'center' }}>
          <div className="big">{s['rule'] === 'renk' ? 'RENK' : 'ŞEKİL'}</div>
          <div className="swatch" style={{ background: color }}>
            {shape}
          </div>
        </div>
      )
    }

    default:
      return <div className="glyph">?</div>
  }
}

/** Sembol arama görevinde hedefi ayrıca göstermek gerekiyor */
export function SearchTarget({ spec }: { spec: TrialSpec | null }) {
  const s = spec?.stimulus as Record<string, unknown> | undefined
  if (!s || s['kind'] !== 'symbol_search') return null
  const t = String(s['target'])
  /**
   * Hedef şekil ızgaranın ÜSTÜNDE ve büyük duruyor.
   *
   * Önce ekranın altındaydı ve küçüktü: çocuk ne aradığını hatırlamak
   * için gözünü ızgaradan ayırıp aşağı bakmak zorunda kalıyordu. Bu,
   * ölçtüğümüz arama süresine her denemede bir göz hareketi ekliyordu.
   */
  return (
    <div className="hedef">
      <span>Aradığın şekil</span>
      <b>{SHAPE_GLYPH[t] ?? t}</b>
    </div>
  )
}

export { COLOR_HEX, SHAPE_GLYPH }
